import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Linkedin, Quote, ChevronDown } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const chipCls =
  "font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#45506b] px-3.5 py-1.5 rounded-full border border-[#e6e1d5] bg-[#faf9f6]";

const BioToggle = ({ expanded, onClick, id }) => (
  <button
    data-testid={`bio-toggle-${id}`}
    onClick={onClick}
    className="group mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#a8824f] hover:text-[#1b2a4e] transition-colors focus:outline-none focus:ring-2 focus:ring-[#a8824f] rounded"
  >
    {expanded ? "Show less" : "Read full bio"}
    <ChevronDown size={14} className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
  </button>
);

const ExpandableBio = ({ id, lead, more, open, toggle }) => (
  <div>
    <p className="text-[#45506b] text-[15px] leading-[1.8]">{lead}</p>
    <AnimatePresence initial={false}>
      {open === id && (
        <motion.div
          key={id}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <p className="text-[#45506b] text-[15px] leading-[1.8] mt-4">{more}</p>
        </motion.div>
      )}
    </AnimatePresence>
    <BioToggle expanded={open === id} onClick={() => toggle(id)} id={id} />
  </div>
);

export default function Team() {
  const [open, setOpen] = useState(null);
  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));

  return (
    <section id="team" data-testid="team-section" className="py-28 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-2xl mb-16">
          <Eyebrow>Our Team</Eyebrow>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1b2a4e] tracking-tight leading-[1.1]">
            Senior counsel. <span className="italic text-[#a8824f]">Rigorous analysis.</span>
          </h2>
          <p className="text-[#45506b] mt-5 text-sm sm:text-base leading-relaxed">
            ComplyWise Consult combines bar-level legal judgement with hands-on analytical support —
            so every engagement gets both strategic direction and meticulous delivery.
          </p>
        </Reveal>

        <div className="space-y-6">
          <Reveal>
            <article
              data-testid="team-member-founder"
              className="rounded-3xl bg-white border border-[#e6e1d5] p-8 sm:p-10 hover:border-[#a8824f]/60 hover:shadow-[0_20px_50px_rgba(27,42,78,0.08)] transition-all duration-300"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div>
                  <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#a8824f] block mb-1.5">
                    Founder &amp; Managing Director
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl text-[#1b2a4e] leading-tight">Janice Acquaah</h3>
                </div>
                <a
                  data-testid="founder-linkedin-link"
                  href="https://www.linkedin.com/in/janice-acquaah-5a3b0a8/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="self-start sm:self-center p-2.5 rounded-full border border-[#e6e1d5] text-[#45506b] hover:text-[#a8824f] hover:border-[#a8824f]/60 transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-[#a8824f]"
                  aria-label="Janice Acquaah on LinkedIn"
                >
                  <Linkedin size={17} />
                </a>
              </div>
              <div className="flex flex-wrap gap-2.5 mb-6">
                {["Ghana Bar · 1995", "England & Wales Bar · 2008", "Financial Crime Prevention", "Governance & Risk"].map((chip) => (
                  <span key={chip} className={chipCls}>{chip}</span>
                ))}
              </div>
              <ExpandableBio
                id="janice"
                open={open}
                toggle={toggle}
                lead="A dual-qualified legal professional — Ghana Bar (1995) and England & Wales Bar (2008) — with over ten years in the City of London's financial services sector, spanning compliance frameworks, financial crime prevention, risk management and governance."
                more="Her work covers regulatory change, policy development, control design, monitoring and advisory support across a wide range of financial institutions: engaging senior stakeholders, managing regulatory interactions and driving improvements in compliance maturity and operational resilience."
              />
              <div className="mt-6 flex items-start gap-3 border-l-2 border-[#a8824f] pl-5 max-w-2xl">
                <Quote size={16} className="text-[#a8824f] shrink-0 mt-1" />
                <p className="font-display italic text-base text-[#1b2a4e] leading-relaxed">
                  Compliance should be embedded into culture, operations and customer outcomes —
                  building a safer, more transparent financial system.
                </p>
              </div>
            </article>
          </Reveal>

          <Reveal delay={0.1}>
            <article
              data-testid="team-member-natalie"
              className="rounded-3xl bg-white border border-[#e6e1d5] p-8 sm:p-10 hover:border-[#a8824f]/60 hover:shadow-[0_20px_50px_rgba(27,42,78,0.08)] transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row gap-8">
                <img
                  data-testid="natalie-portrait"
                  src="/natalie-annan.jpeg"
                  alt="Natalie Annan, Principal Consultant for Business and Product Development at ComplyWise Consult"
                  className="w-40 h-40 sm:w-48 sm:h-48 rounded-2xl object-cover object-top border border-[#e6e1d5] shrink-0"
                />
                <div className="min-w-0">
                  <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#a8824f] block mb-1.5">
                    Principal Consultant — Business &amp; Product Development
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl text-[#1b2a4e] leading-tight mb-5">Natalie Annan</h3>
                  <div className="flex flex-wrap gap-2.5 mb-6">
                    {["Business Development", "Strategy & GTM", "Strategic Partnerships", "E-Learning Platform"].map((chip) => (
                      <span key={chip} className={chipCls}>{chip}</span>
                    ))}
                  </div>
                  <ExpandableBio
                    id="natalie"
                    open={open}
                    toggle={toggle}
                    lead="With a background in Human Resource Management and General Management, and experience in business development, strategy and Go-To-Market execution, Natalie brings a practical, solution-driven approach to business growth and product development."
                    more="At ComplyWise Consult, she leads business and product development: identifying growth opportunities, developing strategic partnerships, shaping new products and driving the firm's digital initiatives — including its e-learning platform for compliance training and professional development."
                  />
                </div>
              </div>
            </article>
          </Reveal>

          <Reveal delay={0.2}>
            <article
              data-testid="team-member-angel-lyric"
              className="rounded-3xl bg-white border border-[#e6e1d5] p-8 sm:p-10 hover:border-[#a8824f]/60 hover:shadow-[0_20px_50px_rgba(27,42,78,0.08)] transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row gap-8">
                <img
                  data-testid="angel-lyric-portrait"
                  src="/angel-lyric-koomson.png"
                  alt="Angel-Lyric Appiah Koomson, Compliance Analyst at ComplyWise Consult"
                  className="w-40 h-40 sm:w-48 sm:h-48 rounded-2xl object-cover object-top border border-[#e6e1d5] shrink-0"
                />
                <div className="min-w-0">
                  <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#a8824f] block mb-1.5">
                    Compliance Analyst
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl text-[#1b2a4e] leading-tight mb-5">Angel-Lyric Appiah Koomson</h3>
                  <div className="flex flex-wrap gap-2.5 mb-6">
                    {["ICA Certified · AML", "KYC & CDD", "Sanctions Risk", "Claims Investigations"].map((chip) => (
                      <span key={chip} className={chipCls}>{chip}</span>
                    ))}
                  </div>
                  <ExpandableBio
                    id="angel"
                    open={open}
                    toggle={toggle}
                    lead="A compliance professional with hands-on experience in due diligence, customer verification, claims investigations and financial crime prevention — helping organisations strengthen compliance processes and uphold regulatory standards."
                    more="As a Consultant to the Official Liquidator through ABG Consult, she reviewed and verified over 5,000 claims submissions, conducted KYC checks and identified potential fraudulent activity. She holds ICA certifications in AML, KYC & CDD, and Managing Sanctions Risk."
                  />
                </div>
              </div>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
